import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, Button, Tabs, Tab, CircularProgress,
    Alert, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton,
} from '@mui/material';
import { Refresh, Close, Visibility, Undo, CheckCircle, Cancel } from '@mui/icons-material';
import api from '../services/api';

const REQUEST_TYPE_LABELS = {
    permanent:     'Permanent room',
    adhoc_weekday: 'Ad hoc room',
    cancellation:  'Room cancellation',
    swap:          'Room swap',
};

const STATUS_COLOURS = {
    pending:  'warning',
    accepted: 'success',
    rejected: 'error',
    reviewed: 'info',
    actioned: 'success',
};

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ORDINALS   = ['', 'First', 'Second', 'Third', 'Fourth', 'Fifth'];

const ROW_BG = {
    available:   { true: '#e8f5e9', false: '#ffebee', null: 'inherit' },
    alternating: { true: '#d4edda', false: '#f5c6cb', null: '#f9f9f9' },
};

function rowBg(available, isOdd) {
    if (available === true)  return isOdd ? '#d4edda' : '#e8f5e9';
    if (available === false) return isOdd ? '#f5c6cb' : '#ffebee';
    return isOdd ? '#f9f9f9' : 'inherit';
}

function describeSessions(sessionArr) {
    if (!sessionArr || !sessionArr.length) return '—';
    return sessionArr.map(s => s === 'all_day' ? 'All day' : s.toUpperCase()).join(', ');
}

function describeSchedule(row) {
    if (row.request_type === 'permanent') {
        if (row.perm_booking_type === 'single') return row.perm_start_date || '—';
        if (row.perm_booking_type === 'series') {
            const rt = row.perm_recurrence_type;
            const rp = row.perm_recurrence_pattern || {};
            if (rt === 'weekly' && rp.weekly) {
                const days = (rp.weekly.days || []).map(d => DAY_SHORT[d]).join(', ');
                return `Every ${rp.weekly.interval || 1}w — ${days || '(no days)'}`;
            }
            if (rt === 'monthly' && rp.monthly) {
                if (rp.monthly.type === 'day_of_month') return `Day ${rp.monthly.day} of each month`;
                return `${ORDINALS[rp.monthly.week_number] || ''} ${DAY_SHORT[rp.monthly.weekday] || ''} of each month`;
            }
            if (rt === 'five_week_rotor' && rp.five_week_rotor) {
                const weeks = (rp.five_week_rotor.weeks || []).join(', ');
                const days  = (rp.five_week_rotor.days_of_week || []).map(d => DAY_SHORT[d]).join(', ');
                return `Rota wks ${weeks} — ${days}`;
            }
            return 'Series';
        }
    }
    if (row.request_type === 'adhoc_weekday') return row.adhoc_wd_date || '—';
    return '—';
}

function describeBookingType(row) {
    if (row.request_type === 'permanent') {
        if (row.perm_booking_type === 'single') return 'Single';
        if (row.perm_booking_type === 'series') return 'Series';
    }
    return '—';
}

function formatDate(ts) {
    if (!ts) return '—';
    return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Detail dialog ─────────────────────────────────────────────────────────────

function DetailDialog({ row, open, onClose }) {
    if (!row) return null;
    const fd = row.form_data || {};

    const field = (label, value) => value ? (
        <Box key={label} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 220, flexShrink: 0 }}>{label}</Typography>
            <Typography variant="body2">{String(value)}</Typography>
        </Box>
    ) : null;

    const sessions  = describeSessions(fd.room_session);
    const roomTypes = [
        ...(fd.room_types || []),
        ...(fd.room_other_type && fd.room_other_type_detail ? [`Other: ${fd.room_other_type_detail}`] : (fd.room_other_type ? ['Other'] : [])),
    ];

    const availLabel = row.available === true  ? '✅ Available'
                     : row.available === false ? '❌ Conflicts found'
                     : '— Not checked';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                {row.reference_number} — Request Details
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Availability</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>{availLabel}</Typography>

                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Submitter</Typography>
                {field('Name', row.name)}
                {field('Email', row.email)}
                {field('Submitted', formatDate(row.created_at))}

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>Request</Typography>
                {field('Request type', REQUEST_TYPE_LABELS[row.request_type] || row.request_type)}
                {field('Booking type', describeBookingType(row))}
                {field('Schedule', describeSchedule(row))}
                {field('Session', sessions)}

                {row.request_type === 'permanent' && <>
                    {field('Preferred clinic', fd.perm_preferred_clinic)}
                    {field('Preferred room', fd.perm_preferred_room)}
                    {field('Could run in different clinic?', fd.perm_different_clinic_reason)}
                    {field('Why cannot be remote?', fd.perm_not_remote_reason)}
                </>}
                {row.request_type === 'adhoc_weekday' && field('Date', fd.adhoc_wd_date)}
                {row.request_type === 'cancellation'  && field('Specialty', fd.cancel_specialty)}
                {row.request_type === 'swap'          && field('Swap details', fd.swap_info)}

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>Room requirements</Typography>
                {field('Number of rooms', fd.room_num_rooms)}
                {roomTypes.length > 0 && field('Room type(s)', roomTypes.join(', '))}
                {field('Nursing support', fd.room_nursing_support === 'yes' ? 'Yes' : fd.room_nursing_support === 'no' ? 'No' : '')}
                {field('Additional info', fd.room_additional_info)}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function RequestsPage() {
    const [rows, setRows]           = useState([]);
    const [tab, setTab]             = useState('pending');
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState('');
    const [updating, setUpdating]   = useState(null);
    const [detailRow, setDetailRow] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = tab !== 'all' ? `?status=${tab}` : '';
            const res = await api.get(`/opra${params}`);
            const data = res.data.map(r => ({
                ...r,
                form_data: typeof r.form_data === 'string' ? JSON.parse(r.form_data) : (r.form_data || {}),
                perm_recurrence_pattern: typeof r.perm_recurrence_pattern === 'string'
                    ? JSON.parse(r.perm_recurrence_pattern)
                    : (r.perm_recurrence_pattern || {}),
                room_session: typeof r.room_session === 'string'
                    ? JSON.parse(r.room_session)
                    : (r.room_session || []),
            }));
            setRows(data);
        } catch {
            setError('Failed to load requests.');
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const setStatus = async (id, status) => {
        setUpdating(id);
        try {
            await api.patch(`/opra/${id}`, { status });
            setRows(prev => prev.map(r => r.id === id ? { ...r, status } : r));
        } catch {
            setError('Failed to update status.');
        } finally {
            setUpdating(null);
        }
    };

    const TABS = [
        { value: 'pending',  label: 'Pending' },
        { value: 'accepted', label: 'Accepted' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'all',      label: 'All' },
    ];

    const HEADERS = ['Reference', 'Request type', 'Single / Series', 'Date / Pattern', 'Session', 'Name', 'Email', 'Submitted', 'Availability', 'Status', ''];

    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">OPRA Requests</Typography>
                <Button startIcon={<Refresh />} onClick={load} size="small" variant="outlined">Refresh</Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                {TABS.map(t => <Tab key={t.value} label={t.label} value={t.value} />)}
            </Tabs>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : rows.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No {tab !== 'all' ? tab : ''} requests found.</Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                                {HEADERS.map(h => (
                                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, idx) => (
                                <TableRow key={row.id} sx={{ bgcolor: rowBg(row.available, idx % 2 !== 0) }}>
                                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                        {row.reference_number || `#${row.id}`}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {REQUEST_TYPE_LABELS[row.request_type] || row.request_type}
                                    </TableCell>
                                    <TableCell>{describeBookingType(row)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{describeSchedule(row)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{describeSessions(row.room_session)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.name}</TableCell>
                                    <TableCell>{row.email}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.created_at)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {row.available === true  && <Chip label="Available"    color="success" size="small" />}
                                        {row.available === false && <Chip label="Conflict"     color="error"   size="small" />}
                                        {row.available === null  && <Chip label="Not checked"  color="default" size="small" />}
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={row.status} color={STATUS_COLOURS[row.status] || 'default'} size="small" />
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Tooltip title="View details">
                                            <IconButton size="small" onClick={() => setDetailRow(row)}>
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        {row.status === 'pending' && (<>
                                            <Tooltip title="Accept">
                                                <span>
                                                    <Button size="small" variant="contained" color="success"
                                                        disabled={updating === row.id}
                                                        startIcon={updating === row.id ? <CircularProgress size={14} color="inherit" /> : <CheckCircle fontSize="small" />}
                                                        onClick={() => setStatus(row.id, 'accepted')}
                                                        sx={{ mr: 0.5, minWidth: 0, px: 1 }}>
                                                        Accept
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Reject">
                                                <span>
                                                    <Button size="small" variant="contained" color="error"
                                                        disabled={updating === row.id}
                                                        startIcon={<Cancel fontSize="small" />}
                                                        onClick={() => setStatus(row.id, 'rejected')}
                                                        sx={{ minWidth: 0, px: 1 }}>
                                                        Reject
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </>)}

                                        {(row.status === 'accepted' || row.status === 'rejected') && (
                                            <Tooltip title="Revert to pending">
                                                <IconButton size="small" color="warning"
                                                    disabled={updating === row.id}
                                                    onClick={() => setStatus(row.id, 'pending')}>
                                                    {updating === row.id ? <CircularProgress size={16} /> : <Undo fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <DetailDialog
                row={detailRow}
                open={Boolean(detailRow)}
                onClose={() => setDetailRow(null)} />
        </Box>
    );
}

export default RequestsPage;
